import { useStore } from '@nanostores/react';
import { VisuallyHidden as RadixVisuallyHidden } from '@radix-ui/react-visually-hidden';
import { memo, useRef, useState, type PropsWithChildren, type ReactNode } from 'react';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { BoltToken } from '~/components/ui/icons/BoltToken';
import { COPIED_MESSAGE_TIMEOUT, REFERRAL_ID_PARAM } from '~/lib/constants';
import { useReferrals } from '~/lib/hooks/useReferralsData';
import { chatStore } from '~/lib/stores/chat';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { userStore } from '~/lib/stores/user';
import { PricingPageOpenedTrigger } from '~/types/analytics';
import { formatTokens } from '~/utils/format';
import { computeReferralTokensAggregate } from '~/utils/tokens-stats';

interface ReferralsDialogContentProps {
  closeDialog: () => void;
}

export const ReferralsDialogContent = memo<ReferralsDialogContentProps>(({ closeDialog }) => {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number>(0);

  const tokenStats = useStore(chatStore.tokenStats);
  const tier = useStore(userStore.getTierStore());

  const { available } = computeReferralTokensAggregate(tokenStats, tier) ?? {};

  const freeReferralCount = tokenStats?.referralTokens?.free.rewardCount;
  const paidReferralCount = tokenStats?.referralTokens?.paid.rewardCount;

  const { rid, freeGrant, subscriptionGrant, loading } = useReferrals();

  const hasRid = !!rid;

  const shareURL = `${window.origin}/?${REFERRAL_ID_PARAM}=${rid}`;

  async function copyShareUrl() {
    clearTimeout(timeoutRef.current);

    setCopied(true);

    await navigator.clipboard.writeText(shareURL);

    setTimeout(() => {
      setCopied(false);
    }, COPIED_MESSAGE_TIMEOUT);
  }

  return (
    <>
      <RadixVisuallyHidden>
        <DialogTitle>Refer Users: Earn Tokens</DialogTitle>
      </RadixVisuallyHidden>
      <DialogDescription noPadding className="p-4 lg:p-6 flex flex-col gap-4 lg:gap-6 text-sm lg:text-base">
        <div className="line-height-0">
          <div className="inline-block p-3 rounded-full bg-bolt-elements-button-primaryLight-background">
            <div className="i-ph:gift size-6 text-bolt-elements-button-primaryLight-text" />
          </div>
        </div>
        <div className="">
          <h2 className="font-semibold text-lg mb-1">Refer Users: Earn Tokens</h2>
          <p className="text-bolt-elements-textSecondary mb-4">
            Earn
            <b className="text-bolt-elements-textPrimary"> {formatTokens(freeGrant)}</b>
            <b className="text-bolt-elements-textPrimary"> tokens </b>
            for yourself & each new user you refer.
          </p>
          <p className="text-bolt-elements-textSecondary">
            Pro users: earn an additional
            <b className="text-bolt-elements-textPrimary"> {formatTokens(subscriptionGrant)}</b>
            <b className="text-bolt-elements-textPrimary"> tokens </b>
            for yourself & your referral when they upgrade to a Pro account within 30 days!
          </p>
        </div>
        <div>
          <TinyCard className="mb-2 lg:mb-4" title="Referral tokens earned" icon={<BoltToken className="size-8" />}>
            {typeof available === 'number' ? formatTokens(available) : <span className="animate-pulse">0</span>}
          </TinyCard>
          <div className="flex gap-2 lg:gap-4">
            <TinyCard
              className="flex-1"
              title="Free Referrals"
              icon={<div className="text-bolt-elements-textTertiary size-8 i-ph:users" />}
            >
              {freeReferralCount ?? <span className="animate-pulse">0</span>}
            </TinyCard>
            <TinyCard
              className="flex-1 relative"
              title="Pro Referrals"
              icon={<div className="text-bolt-elements-textTertiary size-8 i-ph:users" />}
            >
              {tier?.plan === 'personal' && (
                <div className="absolute inset-0 backdrop-blur-[2px] z-10 rounded-lg bg-bolt-elements-background-overlay flex flex-col justify-center items-center text-sm font-normal">
                  <button
                    onClick={() => {
                      closeDialog();
                      pricingStore.openPricingOverlay(PricingPageOpenedTrigger.ReferralsModal);
                    }}
                    className="text-bolt-elements-link hover:text-bolt-elements-linkHover bg-transparent"
                  >
                    Upgrade to Pro
                  </button>
                  <span className="text-sm">to unlock Pro referrals</span>
                </div>
              )}
              {paidReferralCount ?? <span className="animate-pulse">0</span>}
            </TinyCard>
          </div>
          {!hasRid && !loading && (
            <p className="py-3 lg:py-4 text-red-600 h-20.5 lg:h-26.5">
              There was an issue retrieving your referral link. Please try again later.
            </p>
          )}
          {(hasRid || loading) && (
            <div className="h-20.5 lg:h-26.5">
              <p className="py-3 lg:py-4">Use your personal referral link to invite users to join Bolt:</p>
              <div className="flex rounded-lg border-bolt-elements-borderColor border-px bg-bolt-elements-background-depth-4 py-1 lg:py-2 px-2 lg:px-3">
                <input
                  className="flex-grow bg-transparent text-bolt-elements-textTertiary"
                  style={{
                    // outline-none in UnoCSS is not the same as in Tailwind and breaks in Firefox
                    outlineStyle: 'none',
                  }}
                  readOnly
                  value={hasRid ? shareURL : 'Loading your link...'}
                />
                <LegacyButton
                  disabled={!hasRid}
                  variant={hasRid ? 'primary' : 'secondary'}
                  size="sm"
                  className="px-1 lg:px-2 py-0.5 lg:py-1 text-xs relative -right-1 line-height-6"
                  onClick={copyShareUrl}
                >
                  <div className="i-ph:copy size-[15px] mr-1" />
                  {!hasRid && loading ? 'Loading...' : copied ? 'Copied!' : 'Copy link'}
                </LegacyButton>
              </div>
            </div>
          )}
        </div>
      </DialogDescription>
    </>
  );
});

interface TinyCardProps {
  className?: string;
  title: string;
  icon: ReactNode;
}

const TinyCard = ({ className = '', title, icon, children }: PropsWithChildren<TinyCardProps>) => {
  return (
    <div
      className={`p-3 lg:p-4 rounded-lg border-px border-bolt-elements-borderColor bg-bolt-elements-background-depth-1 flex gap-2 lg:gap-4 items-center ${className}`}
    >
      <div className="flex flex-col flex-grow h-12">
        <div className="text-xs text-bolt-elements-textSecondary">{title}</div>
        <div className="text-2xl lg:text-3xl font-bold text-bolt-elements-textPrimary tracking-[-0.6px]">
          {children}
        </div>
      </div>
      {icon}
    </div>
  );
};
